import { useEffect, useState, useCallback } from "react";
import { PrivateAxios } from "../../environment/AxiosInstance";
import { CurrencyList } from "../../utils/StaticData";
import { SuccessMessage, ErrorMessage } from "../../environment/ToastMessage";
import moment from "moment";
import "./TargetPI.css";

const emptyBooking = { customer_name: "", amount: "", po_no: "", booking_date: "" };
const emptySale = { sale_date: "", sales_amount: "", note: "" };

const iso = (v) => (v ? String(v).slice(0, 10) : "");
const dmy = (v) => (v ? moment(v).format("DD-MM-YYYY") : "—");

/**
 * Target PI detail / "User Entry" view for one standalone PI target.
 * Header shows the target name + date range so the user always knows which
 * target they are editing. Booking/sales dates are restricted to that range.
 */
export default function TargetPIModal({ row, canEdit, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newBooking, setNewBooking] = useState(emptyBooking);
  const [newSale, setNewSale] = useState(emptySale);
  const [savingBooking, setSavingBooking] = useState(false);
  const [savingSale, setSavingSale] = useState(false);

  const ccy = detail?.currency || row.currency || "INR";
  const symbol = CurrencyList.find((c) => c.code === ccy)?.symbol || "₹";
  const money = (v) => `${symbol}${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(v) || 0)}`;

  // Target date window (available immediately from the row, refined by detail).
  const fromISO = iso(detail?.from_date || row.from_date);
  const toISO = iso(detail?.to_date || row.to_date);
  const targetName = detail?.target_name || row.target_name;
  const employeeName = detail?.employee_name || row.employee_name;

  const inRange = (d) => {
    if (!d) return false;
    if (fromISO && d < fromISO) return false;
    if (toISO && d > toISO) return false;
    return true;
  };
  const rangeMsg = `Date must be within ${dmy(fromISO)} to ${dmy(toISO)} for "${targetName}".`;

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PrivateAxios.get(`/sales-pi/detail/${row.pi_target_id}`);
      setDetail(res.data?.data || null);
    } catch (err) {
      console.error("Fetch PI detail error:", err);
      ErrorMessage("Failed to load PI detail");
    } finally {
      setLoading(false);
    }
  }, [row.pi_target_id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const summary = detail?.summary || {
    assigned_amount: row.assigned_amount,
    order_booking: 0,
    achievement_sales: 0,
    open_order: 0,
    achievement_pct: 0,
  };

  // ---- Order Booking ----
  const addBooking = async () => {
    if (!newBooking.customer_name.trim()) return ErrorMessage("Customer name is required");
    if (!newBooking.booking_date) return ErrorMessage("Booking date is required");
    if (!inRange(newBooking.booking_date)) return ErrorMessage(rangeMsg);
    setSavingBooking(true);
    try {
      await PrivateAxios.post("/sales-pi/order-booking", {
        pi_target_id: row.pi_target_id,
        customer_name: newBooking.customer_name,
        amount: newBooking.amount || 0,
        po_no: newBooking.po_no,
        booking_date: newBooking.booking_date,
      });
      SuccessMessage("Order booking added");
      setNewBooking(emptyBooking);
      fetchDetail();
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to add order booking");
    } finally {
      setSavingBooking(false);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this order booking?")) return;
    try {
      await PrivateAxios.delete(`/sales-pi/order-booking/${id}`);
      SuccessMessage("Order booking deleted");
      fetchDetail();
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to delete");
    }
  };

  // ---- Achievement-Sales ----
  const addSale = async () => {
    if (!newSale.sale_date) return ErrorMessage("Sale date is required");
    if (!inRange(newSale.sale_date)) return ErrorMessage(rangeMsg);
    setSavingSale(true);
    try {
      await PrivateAxios.post("/sales-pi/sales-entry", {
        pi_target_id: row.pi_target_id,
        sale_date: newSale.sale_date,
        sales_amount: newSale.sales_amount || 0,
        note: newSale.note,
      });
      SuccessMessage("Sales entry added");
      setNewSale(emptySale);
      fetchDetail();
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to add sales entry");
    } finally {
      setSavingSale(false);
    }
  };

  const deleteSale = async (id) => {
    if (!window.confirm("Delete this sales entry?")) return;
    try {
      await PrivateAxios.delete(`/sales-pi/sales-entry/${id}`);
      SuccessMessage("Sales entry deleted");
      fetchDetail();
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to delete");
    }
  };

  const bookings = detail?.orderBookings || [];
  const sales = detail?.salesEntries || [];
  const totalBooking = bookings.reduce((s, b) => s + (Number(b.amount) || 0), 0);
  const totalSales = sales.reduce((s, e) => s + (Number(e.sales_amount) || 0), 0);

  return (
    <div className="pi-modal-overlay" onClick={onClose}>
      <div className="pi-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header: target name + date range, always visible */}
        <div className="pi-modal-header">
          <div className="pi-modal-head-main">
            <div className="pi-modal-target">
              <i className="fas fa-bullseye me-2"></i>
              <span className="pi-modal-target-name">{targetName}</span>
              <span className="pi-modal-emp">· {employeeName}</span>
            </div>
            <div className="pi-modal-range">
              <i className="fas fa-calendar-alt me-1"></i>
              Target period: <strong>{dmy(fromISO)}</strong> &rarr; <strong>{dmy(toISO)}</strong>
            </div>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close"></button>
        </div>

        <div className="pi-modal-body">
          {/* Formula banner */}
          <div className="alert alert-light border d-flex align-items-center gap-2 py-2 mb-3">
            <i className="fas fa-calculator text-secondary"></i>
            <span className="small">
              <strong>Order Booking</strong> − <strong>Achievement (Sales)</strong> = <strong>Open Order</strong>
              <span className="text-muted">  ·  entries must be dated within {dmy(fromISO)} – {dmy(toISO)}</span>
            </span>
          </div>

          {/* Summary strip */}
          <div className="row g-2 mb-3 pi-summary">
            <div className="col">
              <div className="pi-metric">
                <span>Assigned (Target)</span>
                <strong>{money(summary.assigned_amount)}</strong>
              </div>
            </div>
            <div className="col">
              <div className="pi-metric">
                <span>Order Booking</span>
                <strong>{money(summary.order_booking)}</strong>
              </div>
            </div>
            <div className="col">
              <div className="pi-metric">
                <span>Achievement (Sales)</span>
                <strong>{money(summary.achievement_sales)}</strong>
              </div>
            </div>
            <div className="col">
              <div className="pi-metric">
                <span>Open Order</span>
                <strong>{money(summary.open_order)}</strong>
              </div>
            </div>
            <div className="col">
              <div className="pi-metric">
                <span>Achievement %</span>
                <strong>{Number(summary.achievement_pct) || 0}%</strong>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3 text-muted">Loading…</div>}

          <div className="row">
            {/* Order Booking */}
            <div className="col-lg-7 mb-3">
              <div className="pi-table-card">
                <div className="pi-table-title">
                  <i className="fas fa-shopping-cart me-2"></i>Order Booking
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 40 }}>Sr.</th>
                        <th>Customer Name</th>
                        <th className="text-end">Amount</th>
                        <th>PO No.</th>
                        <th>Date</th>
                        {canEdit && <th style={{ width: 50 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 && (
                        <tr>
                          <td colSpan={canEdit ? 6 : 5} className="text-center text-muted">No order bookings yet</td>
                        </tr>
                      )}
                      {bookings.map((b, i) => (
                        <tr key={b.id}>
                          <td>{i + 1}</td>
                          <td>{b.customer_name}</td>
                          <td className="text-end">{money(b.amount)}</td>
                          <td>{b.po_no || "-"}</td>
                          <td>{b.booking_date ? moment(b.booking_date).format("DD-MM-YYYY") : "-"}</td>
                          {canEdit && (
                            <td className="text-center">
                              <button className="btn btn-sm btn-link text-danger p-0" onClick={() => deleteBooking(b.id)} title="Delete">
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bold table-light">
                        <td colSpan={2} className="text-end">Total</td>
                        <td className="text-end">{money(totalBooking)}</td>
                        <td colSpan={canEdit ? 3 : 2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {canEdit && (
                  <div className="pi-add-row">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Customer name"
                      value={newBooking.customer_name}
                      onChange={(e) => setNewBooking({ ...newBooking, customer_name: e.target.value })}
                    />
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="Amount"
                      value={newBooking.amount}
                      onChange={(e) => setNewBooking({ ...newBooking, amount: e.target.value })}
                    />
                    <input
                      className="form-control form-control-sm"
                      placeholder="PO No."
                      value={newBooking.po_no}
                      onChange={(e) => setNewBooking({ ...newBooking, po_no: e.target.value })}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      min={fromISO || undefined}
                      max={toISO || undefined}
                      title={`Allowed: ${dmy(fromISO)} to ${dmy(toISO)}`}
                      value={newBooking.booking_date}
                      onChange={(e) => setNewBooking({ ...newBooking, booking_date: e.target.value })}
                    />
                    <button className="btn btn-sm btn-primary" disabled={savingBooking} onClick={addBooking}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Achievement - Sales */}
            <div className="col-lg-5 mb-3">
              <div className="pi-table-card">
                <div className="pi-table-title">
                  <i className="fas fa-rupee-sign me-2"></i>Achievement − Sales
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th className="text-end">Sales</th>
                        {canEdit && <th style={{ width: 50 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sales.length === 0 && (
                        <tr>
                          <td colSpan={canEdit ? 3 : 2} className="text-center text-muted">No sales entries yet</td>
                        </tr>
                      )}
                      {sales.map((s) => (
                        <tr key={s.id}>
                          <td>{s.sale_date ? moment(s.sale_date).format("DD-MM-YYYY") : "-"}</td>
                          <td className="text-end">{money(s.sales_amount)}</td>
                          {canEdit && (
                            <td className="text-center">
                              <button className="btn btn-sm btn-link text-danger p-0" onClick={() => deleteSale(s.id)} title="Delete">
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bold table-light">
                        <td className="text-end">Total</td>
                        <td className="text-end">{money(totalSales)}</td>
                        {canEdit && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {canEdit && (
                  <div className="pi-add-row">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      min={fromISO || undefined}
                      max={toISO || undefined}
                      title={`Allowed: ${dmy(fromISO)} to ${dmy(toISO)}`}
                      value={newSale.sale_date}
                      onChange={(e) => setNewSale({ ...newSale, sale_date: e.target.value })}
                    />
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="Sales amount"
                      value={newSale.sales_amount}
                      onChange={(e) => setNewSale({ ...newSale, sales_amount: e.target.value })}
                    />
                    <button className="btn btn-sm btn-primary" disabled={savingSale} onClick={addSale}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="pi-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
